/** One headline figure: a label, the value, its unit, what moved and the way to the rows behind it.
 *
 * Lifted out of `ReadinessKpis` so a second screen does not rebuild it. The rules it enforces are
 * the ones that file recorded, and each is something an inline `<div>` would let a caller undo:
 *
 * - **No figure is ever coloured.** Ink on a light tile, near-white on a dark one; the brand green
 *   is chrome and an emissions total is not good news (ADR-0090). The tone changes the surface and
 *   never the figure's hue.
 * - **A figure with a receipt is a button, one without is plain text.** `onOpen` makes it a button
 *   that opens the receipt or breakdown; without it the value is static and does not look like a
 *   link. A figure nothing opens is never dressed as one.
 * - **`delta` is present or absent, never zero.** The caller builds it (it knows the prior period);
 *   an omitted one draws nothing.
 * - **A trend is a thumbnail in a light chip**, whatever the tile's tone, because the chart module
 *   draws in ink-on-surface colours that were measured against a light surface.
 *
 * `figure` and `figureStatic` are the class names `ui-check` finds a clickable KPI by
 * (`[class*="_figure_"]`), so nothing else in this module's stylesheet may start with `figure`.
 */

import type { ReactNode } from "react";

import styles from "./KpiTile.module.css";

export interface KpiTileProps {
  label: string;
  value: string;
  /** A value that is words and not a number (`Not checked`), set smaller so it fits the tile. */
  textual?: boolean;
  unit?: string;
  /** Built by the caller; see the note above. */
  delta?: ReactNode;
  note?: string;
  noteStrong?: string;
  /** Opens where the figure came from. Omitted when there is nothing to open. */
  onOpen?: () => void;
  openLabel?: string;
  /** The link to the rows behind the figure. */
  footer?: ReactNode;
  trend?: ReactNode;
  tone?: "light" | "dark";
  /** `data-kpi`, which the screen tests and the browser gate find a tile by. */
  kpi?: string;
  /** The figure's size: `large` for the one headline total. */
  size?: "normal" | "large";
}

export function KpiTile({
  label,
  value,
  textual,
  unit,
  delta,
  note,
  noteStrong,
  onOpen,
  openLabel,
  footer,
  trend,
  tone = "light",
  kpi,
  size = "normal",
}: KpiTileProps) {
  const figureClass = [
    onOpen ? styles.figure : `${styles.figure} ${styles.figureStatic}`,
    textual ? styles.textual : "",
    size === "large" ? styles.large : "",
    "num",
  ].join(" ");
  const inner = (
    <>
      {value}
      {unit ? <span className={styles.unit}>{unit}</span> : null}
    </>
  );
  return (
    <div className={`${styles.tile} ${styles[tone]}`} data-kpi={kpi}>
      <div className={styles.label}>{label}</div>
      {onOpen ? (
        <button
          type="button"
          className={figureClass}
          onClick={onOpen}
          aria-label={openLabel ?? `${label}: ${value}. Show where this came from`}
        >
          {inner}
        </button>
      ) : (
        <div className={figureClass}>{inner}</div>
      )}
      {delta}
      {note ? (
        <div className={styles.note} title={note}>
          {noteStrong ? <span className={styles.noteStrong}>{noteStrong} </span> : null}
          {note}
        </div>
      ) : null}
      {footer || trend ? (
        <div className={styles.foot}>
          {footer ? (
            <div className={styles.drill} data-testid="kpi-drill">
              {footer}
            </div>
          ) : (
            <span />
          )}
          {trend ? <div className={styles.trend}>{trend}</div> : null}
        </div>
      ) : (
        <div className={styles.foot} />
      )}
    </div>
  );
}
